import { UserProfile } from '_atoms';
import React, { useState } from 'react';
import {
  MdAttachMoney,
  MdClose,
  MdDashboard,
  MdEdit,
  MdEmail,
  MdList,
  MdMenu,
  MdSettings,
  MdSportsHockey,
} from 'react-icons/md';
import { Link, useLocation } from 'react-router-dom';
import main_logo from '../assets/logos/main_logo.png';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navGroups = [
    {
      title: 'Session Management',
      category: 'moderator-only',
      items: [
        {
          path: '/admin/booked-skating-sessions',
          name: 'Booked Sessions',
          icon: <MdList size={20} />,
          category: 'moderator-only',
        },
        {
          path: '/admin/booked-skating-lessons',
          name: 'Booked Skating Lessons',
          icon: <MdList size={20} />,
          category: 'moderator-only',
        },
        {
          path: '/admin/booked-hockey-lessons',
          name: 'Booked Hockey Lessons',
          icon: <MdSportsHockey size={20} />,
          category: 'moderator-only',
        },
      ],
    },
    {
      title: 'Admin Only Settings',
      category: 'admin-only',
      items: [
        {
          path: '/admin/skating-sessions',
          name: 'Skating Sessions',
          icon: <MdSportsHockey size={20} />,
          category: 'admin-only',
        },
        {
          path: '/admin/hockey-lessons',
          name: 'Hockey Lessons',
          icon: <MdSportsHockey size={20} />,
          category: 'admin-only',
        },
        {
          path: '/admin/skating-lessons',
          name: 'Skating Lessons',
          icon: <MdSportsHockey size={20} />,
          category: 'admin-only',
        },
        {
          path: '/admin/season-settings',
          name: 'Season Settings',
          icon: <MdSettings size={20} />,
          category: 'admin-only',
        },
        {
          path: '/admin/season-price-list',
          name: 'Season Price List',
          icon: <MdAttachMoney size={20} />,
          category: 'admin-only',
        },
        {
          path: '/admin/waiver',
          name: 'Waiver',
          icon: <MdEdit size={20} />,
          category: 'admin-only',
        },
        {
          path: '/admin/emails',
          name: 'Emails',
          icon: <MdEmail size={20} />,
          category: 'admin-only',
        },
        {
          path: '/admin/contact-us-messages',
          name: 'Contact Us',
          icon: <MdEmail size={20} />,
          category: 'admin-only',
        },
      ],
    },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:static lg:inset-0`}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <img src={main_logo} alt="logo" className="w-12" />
            <h1 className="text-sm md:text-md font-bold text-gray-900">
              My Dashboard
            </h1>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md hover:bg-gray-100"
          >
            <MdClose size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-4">
          <div className="space-y-6">
            {navGroups.map(group => (
              <div key={group.title}>
                <h3 className="px-4 py-2 mb-2 text-xs font-semibold text-gray-50 uppercase tracking-wider bg-blue-400">
                  {group.title}
                </h3>
                <div className="space-y-1">
                  {group.items.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                        isActive(item.path)
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span
                        className={
                          isActive(item.path)
                            ? 'text-blue-600'
                            : 'text-gray-500'
                        }
                      >
                        {item.icon}
                      </span>
                      <span className="font-medium">{item.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <div className="flex items-center space-x-3 text-sm text-gray-600">
            <MdDashboard size={16} />
            <span>Admin Panel v1.0</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white shadow-sm border-b border-gray-200 h-16 flex items-center justify-between px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-md hover:bg-gray-100"
          >
            <MdMenu size={24} className="text-gray-600" />
          </button>

          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {isActive('/admin/emails') && 'Email Management'}
              {isActive('/admin/contact-us-messages') && 'Contact Us Messages'}
              {isActive('/admin/season-price-list') && 'Season Price List'}
              {isActive('/admin/skating-sessions') && 'Skating Sessions'}
              {isActive('/admin/hockey-lessons') && 'Hockey Lessons'}
              {isActive('/admin/booked-skating-sessions') &&
                'Booked Skating Sessions'}
              {isActive('/admin/booked-skating-lessons') &&
                'Booked Skating Lessons'}
              {isActive('/admin/booked-hockey-lessons') &&
                'Booked Hockey Lessons'}
              {isActive('/admin/season-settings') && 'Season Settings'}
              {isActive('/admin/waiver') && 'Waiver Management'}
            </h2>
          </div>

          <div className="flex items-center space-x-4">
            <Link
              to="/"
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200"
            >
              Back to Main Site
            </Link>
            <UserProfile />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
